import { useMemo, useState } from 'react';

import DataGrid from './DataGrid';
import { DataGridTableId } from '../../../utils/common';

import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalRoot,
  Select,
  TextArea,
  TextInput,
} from '@pella/sash-framework';

import styles from './InputForm.module.css';

const sizeMap = { sm: '600px', md: '900px', lg: '1200px', xl: '1536px' };

/**
 * Handles tab key insertion in multi-line text areas.
 * @param {import('react').KeyboardEvent} e - The keyboard event.
 * @param {boolean} enabled - Whether tab insertion is enabled.
 */
const handleMultiLineTab = (e, enabled) => {
  if (!enabled || e.key !== 'Tab') return;
  e.preventDefault();
  const target = /** @type {HTMLTextAreaElement} */ (e.target);
  const { value, selectionStart, selectionEnd } = target;
  const tab = '\t';
  const newValue = value.slice(0, Math.max(0, selectionStart)) + tab + value.slice(Math.max(0, selectionEnd));
  const newCursorPos = selectionStart + 1;
  // Use nativeInputValueSetter to trigger React's onChange
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
  nativeInputValueSetter?.call(target, newValue);
  target.dispatchEvent(new Event('input', { bubbles: true }));
  requestAnimationFrame(() => {
    target.selectionStart = newCursorPos;
    target.selectionEnd = newCursorPos;
  });
};

/**
 * Modal form component for adding or editing a DataGrid row.
 * @param {object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {() => void} props.toggleDialog - Callback to open/close the dialog
 * @param {(values: object) => void} props.setInputValues - Callback to update form values
 * @param {object} props.inputValues - Current form field values
 * @param {object} props.oldTableData - Original row data before edits
 * @param {Array} props.columns - Column definitions
 * @param {boolean} props.isEdit - Whether in edit mode
 * @param {boolean} props.isCopy - Whether in copy mode
 * @param {string} [props.title] - Dialog title
 * @param {boolean} [props.fullScreen] - Whether the dialog is full screen
 * @param {string} [props.dialogSize] - Dialog size key (sm/md/lg/xl)
 * @param {() => void} props.addUpdateRecord - Callback to submit the form
 * @param {object} [props.errors] - External validation errors by field
 * @param {(inputValues: object, field: string) => boolean | undefined} [props.fieldValidation] - Function to hide fields dynamically
 * @param {() => import('react').ReactNode} [props.headerButtons] - Render function for custom header buttons
 * @param {object} [props.secondaryTable] - Nested table configuration
 * @param {boolean} [props.disableNested] - Whether to hide the nested table
 * @param {Array} [props.rows] - All rows data
 * @param {object} [props.helper] - Helper utilities passed through from DataGrid
 */
const InputForm = (props) => {
  const {
    open,
    toggleDialog,
    setInputValues,
    inputValues,
    oldTableData,
    columns,
    isEdit,
    isCopy,
    title,
    fullScreen,
    dialogSize,
    addUpdateRecord,
    errors = {},
    fieldValidation,
    headerButtons,
    secondaryTable,
    disableNested,
    rows,
  } = props;

  const [nestedTableOverride, setNestedTableOverride] = useState(null);
  const [formFieldErrors, setFormFieldErrors] = useState({});
  const [showSecondaryTable, setShowSecondaryTable] = useState(true);

  // Track previous values of open/isEdit/isCopy to reset form errors on change
  const [prevFormKey, setPrevFormKey] = useState({ open, isEdit, isCopy });
  if (prevFormKey.open !== open || prevFormKey.isEdit !== isEdit || prevFormKey.isCopy !== isCopy) {
    setPrevFormKey({ open, isEdit, isCopy });
    setFormFieldErrors({});
    if (!isEdit && !isCopy) {
      const temp = {};
      for (const c of columns) temp[c.field] = c.defaultValue;
      setInputValues(temp);
    }
  }

  const secondaryTableData = useMemo(() => {
    if (nestedTableOverride) return nestedTableOverride;
    const dataField = String(secondaryTable?.dataField);
    const data = oldTableData[dataField];
    return data ? DataGridTableId(data) : [];
  }, [oldTableData, secondaryTable?.dataField, nestedTableOverride]);

  // Reset override when oldTableData changes so derived value takes over again
  const [prevOldTableData, setPrevOldTableData] = useState(oldTableData);
  if (prevOldTableData !== oldTableData) {
    setPrevOldTableData(oldTableData);
    if (nestedTableOverride !== null) setNestedTableOverride(null);
  }

  const setUserInput = (field, value) => setInputValues({ ...inputValues, [field]: value });
  const closeDialog = () => {
    toggleDialog();
  };

  // Small hack to refresh nested data tables.
  const resetNestedTable = () => {
    const dataField = String(secondaryTable?.dataField);
    const tempData = inputValues[dataField];
    setShowSecondaryTable(false);
    setTimeout(() => {
      setNestedTableOverride(structuredClone(tempData));
      setShowSecondaryTable((show) => !show);
    }, 500);
  };

  const formCheck = () => {
    let hasError = false;
    const fieldValidationErrors = {};
    for (const column of columns) {
      if (column?.required && !inputValues[column.field] && inputValues[column.field] !== 0) {
        hasError = true;
        fieldValidationErrors[column.field] = 'This field is required';
      }
    }
    setFormFieldErrors(fieldValidationErrors);
    return hasError;
  };

  const submitInputForm = () => {
    if (formCheck()) {
      return;
    }
    addUpdateRecord();
  };

  const filterFields = (c) => {
    if ((!isEdit && c.editable === 'never') || c.editable === false) {
      return false;
    }
    if (c.readonly) return true;
    return c.hide !== true;
  };

  const checkSelectedValue = (column, value) => {
    if (column.setValue) {
      column.setValue(value);
    }
    setUserInput(column.field, value);
  };

  const modalWidth = sizeMap[dialogSize] || '900px';
  const AnyModalHeader = /** @type {import('react').ElementType} */ (ModalHeader);

  return (
    <ModalRoot open={open} onOpenChange={closeDialog}>
      <Modal fullScreen={fullScreen === true} width={modalWidth} showClose={false}>
        <AnyModalHeader
          className={fullScreen ? `${styles.modalHeader} ${styles.fullScreenHeader}` : styles.modalHeader}
        >
          {title}
          {fullScreen && (
            <>
              <Button
                size='sm'
                color='secondary'
                onClick={closeDialog}
                className={styles.headerButton}
                iconLeft='close'
              >
                Close
              </Button>
              <Button
                size='sm'
                color='primary'
                onClick={submitInputForm}
                className={styles.headerButton}
                iconLeft='save'
              >
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </>
          )}
          {headerButtons ? headerButtons() : null}
        </AnyModalHeader>
        <ModalContent>
          <div className={styles.fieldsGrid}>
            {columns
              ?.filter((c) => filterFields(c))
              .map((col, index) => (
                <div
                  className={styles.fieldCell}
                  key={col.field}
                  hidden={fieldValidation ? fieldValidation(inputValues, col.field) : undefined}
                >
                  {!col.valueOptions && !col.multiLine && (
                    <TextInput
                      disabled={
                        col.editable === 'never' || (col.editable === 'onAdd' && isEdit) || col.readonly === true
                      }
                      error={
                        formFieldErrors[col.field] ||
                        errors[col.field] ||
                        (col.required && !inputValues[col.field] ? 'This field is required' : '') ||
                        undefined
                      }
                      id={`input_${index}_${col.field}`}
                      labelText={col.headerName}
                      type={col.type === 'numeric' ? 'number' : 'text'}
                      value={inputValues[col.field] || ''}
                      onChange={(e) => setUserInput(col.field, e.target.value)}
                      className={styles.fullWidth}
                      required={col.required}
                      {...(col.inputProps || {})}
                    />
                  )}
                  {!col.valueOptions && col.multiLine && (
                    <TextArea
                      disabled={
                        col.editable === 'never' || (col.editable === 'onAdd' && isEdit) || col.readonly === true
                      }
                      error={
                        formFieldErrors[col.field] ||
                        errors[col.field] ||
                        (col.required && !inputValues[col.field] ? 'This field is required' : '') ||
                        undefined
                      }
                      id={`input_${index}_${col.field}`}
                      labelText={col.headerName}
                      value={inputValues[col.field] || ''}
                      onChange={(e) => setUserInput(col.field, e.target.value)}
                      onKeyDown={(e) => handleMultiLineTab(e, col.multiLine)}
                      required={col.required}
                    />
                  )}
                  {col.valueOptions && (
                    <Select
                      id={`${index}_${col.field}`}
                      labelText={col.headerName}
                      disabled={
                        col.editable === 'never' || (col.editable === 'onAdd' && isEdit) || col.readonly === true
                      }
                      required={col.required}
                      error={
                        formFieldErrors[col.field] ||
                        errors[col.field] ||
                        (col.required && !inputValues[col.field] ? 'This field is required' : '') ||
                        undefined
                      }
                      value={inputValues[col.field] !== undefined ? inputValues[col.field] : ''}
                      onChange={(e) => checkSelectedValue(col, e.target.value)}
                      className={styles.fullWidth}
                    >
                      {(typeof col.valueOptions === 'function' ? col.valueOptions(rows) : col.valueOptions).map(
                        (val) => (
                          <option
                            key={`${col.field}_${val.value !== undefined ? val.value : val}`}
                            value={val.value !== undefined ? val.value : val}
                          >
                            {val.label || val}
                          </option>
                        ),
                      )}
                    </Select>
                  )}
                </div>
              ))}
          </div>
          {!isCopy && secondaryTable && disableNested === false && showSecondaryTable && (
            <div>
              <DataGrid
                rows={secondaryTableData}
                parentId={oldTableData[String(secondaryTable?.parentIdField)]}
                resetValues={resetNestedTable}
                {...secondaryTable}
              />
            </div>
          )}
        </ModalContent>
        {!fullScreen && (
          <ModalFooter>
            <Button
              size='sm'
              color='secondary'
              className={styles.roAttbsRedButt}
              onClick={closeDialog}
              iconLeft='close'
            >
              Close
            </Button>
            <Button
              size='sm'
              color='primary'
              onClick={submitInputForm}
              className={styles.roAttbsGreenButt}
              iconLeft='check_circle'
            >
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </ModalRoot>
  );
};

export default InputForm;
